import React from 'react';
import styled from '@benzinga/themetron';
import { MenuInterface, MenuProps, GroupItem, MenuItem } from './interfaces';
import { Icon } from '@benzinga/core-ui';

import { faGraduationCap } from '@fortawesome/pro-regular-svg-icons/faGraduationCap';
import { faWrench } from '@fortawesome/pro-solid-svg-icons/faWrench';
import { faChartMixed } from '@fortawesome/pro-solid-svg-icons/faChartMixed';
import { faChalkboard } from '@fortawesome/pro-solid-svg-icons/faChalkboard';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import classNames from 'classnames';
import { convertToRelativeUrl } from './_mockMenu';
import { ButtonBzPro } from '../BenzingaProButton';
import { useIsUserLoggedIn } from '@benzinga/user-context';
import Hooks from '@benzinga/hooks';
import { appEnvironment, appName } from '@benzinga/utils';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import { handleMenuItemsClickEvent } from '../../utils';

interface MenuItemGroupProps {
  groups: GroupItem[];
}

export const Menu: React.FC<MenuProps> = React.memo(({ menu, secondary = false }) => {
  const hasSubmenu = menu.groups?.length || menu.subnav;
  const hasGroupsAndSubnav = Boolean(menu.groups?.length && menu.subnav);
  const isTradingSchool = menu.label === 'Trading School';
  const isLoggedIn = Hooks.useHydrate(useIsUserLoggedIn(), false);
  const isMoneyApp = appEnvironment().isApp(appName.money);

  return (
    <MenuWrapper className={`menu-wrapper ${secondary ? 'secondary' : 'primary'}`}>
      {secondary ? (
        <PrimaryToolbarButton
          $background={menu.color}
          aria-label={`This opens ${menu.label} in a new tab`}
          className="others-btn primary-toolbar-button"
          href={isLoggedIn && menu.logged_in_href ? menu.logged_in_href : menu.href}
          onClick={() => handleMenuItemsClickEvent('menu_click', 'main_menu', menu.label)}
          style={{ minWidth: menu.width ?? 'fit-content' }}
          target="_blank"
          title={menu.label}
        >
          {menu.label === 'Tools' && <Icon className="text-xl pr-1" icon={faWrench} />}
          {menu.label === 'My Stocks' && <Icon className="text-xl pr-1" icon={faChartMixed} />}
          {menu.label === 'Research' && <Icon className="text-xl pr-2" icon={faChalkboard} />}
          {menu.label}
        </PrimaryToolbarButton>
      ) : (
        <MenuItemLink
          className={classNames(`menu-item-link`, {
            [`${menu.className}`]: !!menu.className,
            initial: !isTradingSchool,
            'trading-school': isTradingSchool,
          })}
          data-action="Navigation Main Menu Click"
          href={convertToRelativeUrl(isLoggedIn && menu.logged_in_href ? menu.logged_in_href : menu.href)}
          onClick={() => handleMenuItemsClickEvent('menu_click', 'main_menu', menu.label)}
          style={{ minWidth: menu.width ?? 'fit-content' }}
          title={menu.label}
        >
          {menu.label} {isTradingSchool && <Icon className="ml-2 text-2xl" icon={faGraduationCap} />}
          {hasSubmenu && menu.subGroupChevron && (
            <Icon
              className="ml-2 test-xs text-gray-400 menu-item-link-chevron"
              icon={faChevronDown}
              style={{ height: '10px', width: '9px' }}
              trimX={true}
              trimY={true}
            />
          )}
        </MenuItemLink>
      )}
      {hasSubmenu ? (
        <SubNavWrapper
          className={classNames(
            'sub-nav-wrapper subnav',
            { 'has-groups': !!menu?.groups },
            { 'money-app': isMoneyApp },
            { 'tools-group': menu.label === 'Tools' },
          )}
        >
          <SubGroupWrapper className="sub-group-wrapper" hasGroupsAndSubnav={hasGroupsAndSubnav}>
            {menu.label === 'Tools' && (
              <ButtonBzPro
                label="Free Benzinga Pro Trial"
                url="https://pro.benzinga.com/register/?utm_source=tools-bz-pro-free-trial"
                variant="trial"
              />
            )}
            {menu.subnav ? <MenuItems items={menu.subnav.links} /> : null}
            {menu.groups?.length ? <MenuItemGroups groups={menu.groups} /> : null}
          </SubGroupWrapper>
        </SubNavWrapper>
      ) : null}
    </MenuWrapper>
  );
});

interface MenuItemsProps {
  items: MenuItem[];
}

export const MenuItems: React.FC<MenuItemsProps> = ({ items }) => {
  return (
    <div className="menu-items">
      <MenuItemWrapper className="menu-item-wrapper">
        {items?.map((item, i: number) => {
          const dataAction = item.action ? item.action : 'Navigation Sub-Menu Click';
          return (
            <DropdownMenuItem
              className={item.highlight ? 'highlight dropdown-menu-item' : 'dropdown-menu-item'}
              highlight={item.highlight}
              key={i}
            >
              <a
                data-action={dataAction}
                href={convertToRelativeUrl(item.href)}
                onClick={() => handleMenuItemsClickEvent('menu_click', 'main_menu', item.label)}
                title={item.title}
              >
                {item.label}
              </a>
              {item?.subnav?.links && (
                <>
                  <span className="more-nav-menu">
                    <Icon className="arrow-right-icon" icon={faAngleRight} />
                  </span>
                  <div className="third-level-menu">
                    {item?.subnav?.links ? <MenuItems items={item.subnav.links} /> : null}
                  </div>
                </>
              )}
            </DropdownMenuItem>
          );
        })}
      </MenuItemWrapper>
    </div>
  );
};

const MenuItemGroups: React.FC<MenuItemGroupProps> = ({ groups }) => {
  return (
    <GroupList className="group-list">
      {groups?.map((group, i) => {
        return (
          <div key={i}>
            <GroupListItemLabel className="group-list-item-label">{group.label}</GroupListItemLabel>
            <div>
              <MenuItems items={group.links} />
            </div>
          </div>
        );
      })}
    </GroupList>
  );
};

export const MenuItemLink = styled.a`
  line-height: 48px;
  padding: 0 10px;
  font-family: Manrope, Manrope-fallback, sans-serif;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colorPalette.black};
  height: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme }) => theme.colorPalette.blue100};
    color: unset;
  }

  &.trading-school {
    color: #298ccd !important;
  }

  @media screen and (min-width: 1292px) {
    &.trading-school {
      svg {
        display: none;
      }
    }
  }

  @media screen and (max-width: 800px) {
    color: white;
    width: 100%;
    text-align: left;
    display: inline-flex;
    justify-content: start;
    &:hover {
      background-color: inherit;
    }
  }
`;

const MenuItemWrapper = styled.div`
  line-height: 2em;
  a {
    color: black;
    &:hover {
      color: ${({ theme }) => theme.colorPalette.blue700};
    }
  }
`;

const DropdownMenuItem = styled.div<{ highlight?: string }>`
  line-height: 2em;

  a {
    display: block;
    padding: 6px 12px;
    line-height: 1.4rem;
  }

  &:hover {
    color: #1a79ff;
  }

  &.highlight {
    padding: 0 5px;
    margin-bottom: 5px;

    a {
      color: #ffffff;
      background-color: ${({ highlight }) => (highlight ? highlight : 'unset')};
      font-weight: ${({ highlight, theme }) => (highlight ? theme.fontWeight.semibold : 'unset')};
      text-align: center;
    }

    &:hover {
      color: ${({ highlight }) => (highlight ? '#ffffff' : 'unset')};
    }
  }
`;

const GroupList = styled.div`
  display: grid;
  gap: 8px;
  grid-template-columns: repeat(2, minmax(0px, 1fr));
`;

const GroupListItemLabel = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colorPalette.gray300};
  color: ${({ theme }) => theme.colorPalette.gray600};
  font-weight: bold;
  margin-top: ${({ theme }) => theme.space['1']};
  margin-bottom: ${({ theme }) => theme.space['1']};
  margin-left: ${({ theme }) => theme.space['4']};
  margin-right: ${({ theme }) => theme.space['4']};
  padding-bottom: ${({ theme }) => theme.space['1']};
`;

const MoreMenuItemsDropdownContainer = styled.div<{ isVisible?: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'flex' : 'none')};
  position: absolute;
  width: max-content;
  top: 48px;
  z-index: 2000;
  background-color: ${({ theme }) => theme.colorPalette.white};
  box-shadow: ${({ theme }) => theme.shadow.md};
  flex-direction: row;
  flex-wrap: wrap;

  &:hover {
    display: flex;
  }

  .menu-wrapper {
    a {
      width: 100%;
      justify-content: left;
    }
    .subnav {
      right: auto;
    }
  }

  @media screen and (min-width: 910px) {
    min-width: initial;
  }

  > div {
    @media screen and (min-width: 930px) {
      &:nth-of-type(1) {
        display: none;
      }
    }

    @media screen and (min-width: 1010px) {
      &:nth-of-type(2) {
        display: none;
      }
    }

    @media screen and (min-width: 1090px) {
      &:nth-of-type(3) {
        display: none;
      }
    }

    @media screen and (min-width: 1190px) {
      &:nth-of-type(4) {
        display: none;
      }
    }

    @media screen and (min-width: 1240px) {
      &:nth-of-type(5) {
        display: none;
      }
    }

    @media screen and (min-width: 1300px) {
      &:nth-of-type(6) {
        display: none;
      }
    }

    @media screen and (min-width: 1360px) {
      &:nth-of-type(7) {
        display: none;
      }
    }
  }
`;

export const MoreMenuItemsDropdown = ({ isVisible, menus }) => {
  const hiddenMenuItems = menus.primary?.slice(2) || [];

  return (
    <MoreMenuItemsDropdownContainer className="more-menu-items-dropdown-container" isVisible={isVisible}>
      {hiddenMenuItems.map((menu: MenuInterface, i: number) => (
        <Menu key={i} menu={menu} />
      ))}
    </MoreMenuItemsDropdownContainer>
  );
};

const SubGroupWrapper = styled.div<{ hasGroupsAndSubnav?: boolean }>`
  &.sub-group-wrapper {
    background-color: ${({ theme }) => theme.colorPalette.white};
    border-width: 1px;
    border-top-width: 1px;
    border-color: ${({ theme }) => theme.colorPalette.gray200};
    border-bottom-left-radius: ${({ theme }) => theme.borderRadius.sm};
    border-bottom-right-radius: ${({ theme }) => theme.borderRadius.sm};
    padding: 8px 0;
    scrollbar-width: thin;
    max-height: calc(90vh - 100px);
    // overflow: auto;
    ::-webkit-scrollbar {
      width: 6px !important;
    }
    ::-webkit-scrollbar-track {
      background: #f1f1f1;
    }
    ::-webkit-scrollbar-thumb {
      background: #888;
    }
    ::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
  }
`;

const MenuWrapper = styled.div`
  &.secondary {
    position: relative;
    .subnav {
      position: absolute;
      right: 0;
      top: 48px;
    }
  }
  .subnav {
    display: none;
    position: absolute;
    z-index: 2000;
    .third-level-menu {
      display: none;
      position: absolute;
      left: 100%;
      background: #ffffff;
      border: 1px solid ${({ theme }) => theme.colorPalette.gray200};
      border-bottom-left-radius: ${({ theme }) => theme.borderRadius.sm};
      border-bottom-right-radius: ${({ theme }) => theme.borderRadius.sm};
      height: 100%;
      z-index: 999999;
      overflow: auto;
      top: 0;
      height: auto;
      min-width: 160px;
    }
    .more-nav-menu {
      position: absolute;
      top: 50%;
      right: 0;
      transform: translateY(-50%);
    }
  }
  &:hover {
    .subnav {
      display: block;
    }
  }
  .menu-items {
    padding: 4px;
    .dropdown-menu-item {
      position: relative;
      &:hover {
        .third-level-menu {
          display: block;
        }
      }
    }
  }
  @media screen and (max-width: 800px) {
    .subnav {
      display: none !important;
    }
  }
`;

const SubNavWrapper = styled.div`
  text-align: left;

  &.has-groups {
    width: 480px;
    &.money-app {
      max-width: 480px;
      width: auto;
      min-width: 180px;
    }
  }
  &.tools-group {
    width: 720px;
    .sub-group-wrapper {
      overflow-y: auto;
    }
    .group-list {
      grid-template-columns: repeat(3, minmax(0px, 1fr));
    }
  }
`;

interface PrimaryWrapperProps {
  $background: React.CSSProperties['background'];
}

const PrimaryToolbarButton = styled.a<PrimaryWrapperProps>`
  background-color: ${props => props.$background};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.sm}; // fontSize="sm"
  font-weight: ${({ theme }) => theme.fontWeight.bold}; // fontWeight="bold"
  white-space: nowrap;

  &.others-btn {
    height: 48px;
    color: ${({ theme }) => theme.colorPalette.gray700};
    text-transform: capitalize;
    padding: 0 8px;
    &:hover {
      color: ${({ theme }) => theme.colorPalette.blue700};
    }
  }

  &.research-btn {
    height: 34px;
    color: white;
    text-transform: uppercase;
    border-radius: 5px;
    letter-spacing: 1px;
    padding: 0 14px;
    &:hover {
      opacity: 100;
      color: white;
    }
  }
  &.invest-in-art-btn {
    height: 34px;
    color: #000000;
    border-radius: 5px;
    letter-spacing: 1px;
    padding: 0 10px;
    margin-right: 8px;
    &:hover {
      background-color: ${({ theme }) => theme.colorPalette.blue300};
    }
  }
`;
